import { collection, doc, onSnapshot, Unsubscribe } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import firebaseFirestore from '~/config/firebase/firestore';
import type { FichaCliente } from './BuscarCliente';

/**
 * El directorio de clientes que mantiene el servidor.
 *
 * Antes, para buscar a alguien, cada navegador se bajaba reservas: las 400
 * últimas costaban 2 s, y traerlas todas eran 11.025 lecturas y 8,6 segundos
 * CADA VEZ que se estrenaba un equipo. Ahora una Cloud Function mantiene una
 * libreta con una ficha por persona y el panel solo se baja eso: unos pocos
 * documentos, una vez por sesión.
 *
 * Se queda escuchando mientras la página esté abierta, así que si alguien
 * corrige el teléfono de un cliente, el resto lo ve sin recargar. Ese era el
 * punto flaco de guardar la libreta en el navegador: nunca se enteraba.
 *
 * Y no se guarda nada en el disco del equipo. En NOI comparten computadores;
 * dejar ahí los datos personales de 2.455 clientes habría sido peor que el
 * problema que resuelve.
 */

/** Un trozo del directorio, tal y como lo escribe la función. */
type FichaCompacta = {
  k: string;
  n: string;
  c?: string;
  e?: string;
  d?: string;
  t?: string;
  a?: string;
  x?: Record<string, unknown>;
  u?: number;
  v?: number;
};

export type EstadoDirectorio =
  /** Todavía bajándose. */
  | 'cargando'
  /** Listo para buscar. */
  | 'listo'
  /** Esta empresa no tiene directorio: hay que tirar del respaldo. */
  | 'ausente'
  /** Algo falló (permisos, red): también se tira del respaldo. */
  | 'error';

export type Directorio = {
  estado: EstadoDirectorio;
  fichas: FichaCliente[];
  /** Cuándo se actualizó por última vez, para poder decirlo en pantalla. */
  actualizado?: Date;
};

const VACIO: Directorio = { estado: 'cargando', fichas: [] };

const desdeCompacta = (f: FichaCompacta): FichaCliente => ({
  clave: f.k,
  nombre: f.n,
  empresa: f.c,
  correo: f.e,
  documento: f.d,
  telefono: f.t,
  direccion: f.a,
  extra: (f.x ?? {}) as Record<string, unknown>,
  ultima: f.u ? new Date(f.u) : undefined,
  cuantas: f.v ?? 1,
});

/**
 * Una sola suscripción por empresa para toda la página.
 *
 * La modal se abre y se cierra muchas veces; volver a bajarse el directorio
 * en cada apertura sería tirar lecturas a la basura. Se guarda aquí, fuera de
 * React, y quien lo necesite se engancha.
 */
type Suscripcion = {
  directorio: Directorio;
  oyentes: Set<(d: Directorio) => void>;
  cortar: Unsubscribe[];
};

const suscripciones = new Map<string, Suscripcion>();

const avisar = (s: Suscripcion) => {
  s.oyentes.forEach((oyente) => oyente(s.directorio));
};

const arrancar = (companyId: string): Suscripcion => {
  const yaEsta = suscripciones.get(companyId);
  if (yaEsta) return yaEsta;

  const s: Suscripcion = { directorio: VACIO, oyentes: new Set(), cortar: [] };
  suscripciones.set(companyId, s);

  const refMeta = doc(firebaseFirestore, 'client_directory', companyId);

  // Los trozos, por separado: Firestore solo cobra lectura de los que
  // realmente cambian, así que una corrección cuesta una lectura, no todo el
  // directorio otra vez.
  const porTrozo = new Map<string, FichaCliente[]>();

  const rehacer = (estado: EstadoDirectorio, actualizado?: Date) => {
    // Si una reconstrucción cambió el número de trozos, una misma persona
    // podría aparecer en dos sitios hasta que el sobrante se borre. Gana la
    // ficha con la reserva más reciente.
    const porClave = new Map<string, FichaCliente>();
    porTrozo.forEach((fichas) => {
      fichas.forEach((f) => {
        const previa = porClave.get(f.clave);
        const gana =
          !previa || (f.ultima?.getTime() ?? 0) >= (previa.ultima?.getTime() ?? 0);
        if (gana) porClave.set(f.clave, f);
      });
    });
    s.directorio = {
      estado,
      fichas: [...porClave.values()],
      actualizado: actualizado ?? s.directorio.actualizado,
    };
    avisar(s);
  };

  const fallo = (estado: EstadoDirectorio) => {
    s.directorio = { ...s.directorio, estado };
    avisar(s);
  };

  s.cortar.push(
    onSnapshot(
      refMeta,
      (snap) => {
        if (!snap.exists()) {
          fallo('ausente');
          return;
        }
        const cuando = snap.data()?.actualizado;
        rehacer('listo', typeof cuando === 'number' ? new Date(cuando) : undefined);
      },
      () => fallo('error')
    )
  );

  s.cortar.push(
    onSnapshot(
      collection(refMeta, 'parts'),
      (snap) => {
        snap.docChanges().forEach((cambio) => {
          if (cambio.type === 'removed') {
            porTrozo.delete(cambio.doc.id);
            return;
          }
          const fichas = (cambio.doc.data()?.fichas ?? []) as FichaCompacta[];
          porTrozo.set(cambio.doc.id, fichas.map(desdeCompacta));
        });
        rehacer(porTrozo.size ? 'listo' : 'ausente');
      },
      () => fallo('error')
    )
  );

  return s;
};

/** Engancha un componente al directorio de su empresa. */
export const useDirectorioClientes = (
  companyId: string | undefined,
  activo: boolean
): Directorio => {
  const [directorio, setDirectorio] = useState<Directorio>(VACIO);

  useEffect(() => {
    if (!activo || !companyId) return undefined;
    const s = arrancar(companyId);
    setDirectorio(s.directorio);
    s.oyentes.add(setDirectorio);
    return () => {
      s.oyentes.delete(setDirectorio);
    };
  }, [companyId, activo]);

  return directorio;
};

/**
 * Suelta lo que hubiera en memoria.
 *
 * Al cerrar sesión no debe quedar la libreta de clientes flotando en la
 * pestaña: si el siguiente que entra es otra persona, no es asunto suyo.
 */
export const olvidarDirectorios = () => {
  suscripciones.forEach((s) => {
    s.cortar.forEach((cortar) => cortar());
    s.oyentes.clear();
  });
  suscripciones.clear();
};
